import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { friendlyError } from "@/lib/friendlyError";

export interface MigrationRequest {
  id: string;
  nursery_id: string;
  submitted_by_user_id: string;
  status: "submitted" | "in_review" | "in_progress" | "completed" | "cancelled";
  current_system: string | null;
  children_count: number | null;
  staff_count: number | null;
  zones_to_migrate: string[] | null;
  notes: string | null;
  file_paths: string[] | null;
  submitted_at: string;
  reviewed_at: string | null;
  completed_at: string | null;
  assigned_to: string | null;
  admin_notes: string | null;
  completion_notified_at: string | null;
  created_at: string;
  updated_at: string;
  // joined
  nurseries?: { name: string };
  profiles?: { email: string; first_name: string; last_name: string };
}

export function useMigrationRequest() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["migration-request"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("migration_requests")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) throw error;
      return (data && data.length > 0 ? data[0] : null) as MigrationRequest | null;
    },
    enabled: !!user,
  });
}

export function useSubmitMigration() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (formData: {
      currentSystem: string;
      childrenCount?: number;
      staffCount?: number;
      zonesToMigrate: string[];
      notes?: string;
      filePaths: string[];
    }) => {
      const { data, error } = await supabase.functions.invoke("submit-migration-request", {
        body: formData,
      });

      if (error) throw error;
      if (data?.error) {
        if (data.error === "professional_required") {
          throw new Error("professional_required");
        }
        if (data.error === "migration_already_in_progress") {
          throw new Error("migration_already_in_progress");
        }
        throw new Error(data.error);
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["migration-request"] });
      toast({ title: "Migration request submitted successfully ✅" });
    },
    onError: (err: Error) => {
      if (err.message !== "professional_required" && err.message !== "migration_already_in_progress") {
        toast({ title: "Could not send your migration request", description: friendlyError(err), variant: "destructive" });
      }
    },
  });
}

// Admin hooks
export function useAllMigrationRequests() {
  return useQuery({
    queryKey: ["admin-migration-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("migration_requests")
        .select("*, nurseries(name)")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []) as MigrationRequest[];
    },
  });
}

export function useUpdateMigrationStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, status, adminNotes }: { id: string; status: string; adminNotes?: string }) => {
      const updates: Record<string, unknown> = { status };
      if (status === "in_review") updates.reviewed_at = new Date().toISOString();
      if (adminNotes !== undefined) updates.admin_notes = adminNotes;

      const { error } = await supabase
        .from("migration_requests")
        .update(updates as any)
        .eq("id", id);

      if (error) throw error;

      // Audit
      await supabase.from("audit_log").insert({
        description: `Migration request status updated to ${status}`,
        record_type: "migration_request",
        record_id: id,
        user_name: "Nursly Admin",
        user_role: "nursly_admin",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-migration-requests"] });
      toast({ title: "Migration status updated ✅" });
    },
    onError: (err: Error) => {
      toast({ title: "Could not update the status", description: friendlyError(err), variant: "destructive" });
    },
  });
}

export function useCompleteMigration() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ migrationRequestId, adminNotes }: { migrationRequestId: string; adminNotes?: string }) => {
      const { data, error } = await supabase.functions.invoke("complete-migration", {
        body: { migrationRequestId, adminNotes },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-migration-requests"] });
      toast({ title: "Migration marked as complete. Nursery has been notified. ✅" });
    },
    onError: (err: Error) => {
      toast({ title: "Could not finish the migration", description: friendlyError(err), variant: "destructive" });
    },
  });
}

const NURSLY_ADMIN_EMAIL = "timiadegbenla@gmail.com";

export function useIsNurslyAdmin() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["is-nursly-admin", user?.id],
    queryFn: async () => {
      if (!user) return false;
      // Hard-coded email restriction, admin panel is exclusively for Timi
      if ((user.email ?? "").toLowerCase() !== NURSLY_ADMIN_EMAIL) return false;
      const { data, error } = await supabase
        .from("profiles")
        .select("is_nursly_admin")
        .eq("user_id", user.id)
        .single();
      if (error) return false;
      return data?.is_nursly_admin === true;
    },
    enabled: !!user,
    staleTime: 300_000,
  });
}
